import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add, FilterList, Clear, Search, Description } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { studentAPI, sessionAPI } from '../services/api';
import { useClearOnSiteChange, useCurrentSiteId } from '../hooks/useCurrentSiteId';
import { format, parseISO, isValid } from 'date-fns';
import { passesMedicalDueFilter, MEDICAL_DUE_OPTIONS } from '../utils/medicalDueFilter';
import AppHeader from './AppHeader';
import NewStudentForm from './NewStudentForm';

function formatListDate(value) {
  if (value == null || value === '') return '-';
  try {
    const d = typeof value === 'string' ? parseISO(value.slice(0, 10)) : value;
    if (!isValid(d)) return String(value);
    return format(d, 'MMMM d, yyyy');
  } catch {
    return String(value);
  }
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'graduated', label: 'Graduated' },
  { value: 'transferred', label: 'Transferred' },
];

// Packet overall status (from /sessions/packet-dashboard/) → display + filter.
const PACKET_STATUS_OPTIONS = [
  { value: '', label: 'All packet statuses' },
  { value: 'complete', label: 'Completed only' },
  { value: 'not_complete', label: 'Not completed (sent or not sent)' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'not_sent', label: 'Not sent' },
];

const PACKET_STATUS_LABELS = {
  complete: 'Complete',
  in_progress: 'In progress',
  not_sent: 'Not sent',
};

const StudentList = () => {
  const { user } = useAuth();
  const siteId = useCurrentSiteId();
  const navigate = useNavigate();
  // Filter state is mirrored into URL query params so back-navigation from a
  // student profile restores the prior filtered view (Sori Klein item 3).
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterLastName, setFilterLastName] = useState(() => searchParams.get('last') || '');
  const [filterFirstName, setFilterFirstName] = useState(() => searchParams.get('first') || '');
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || '');
  const [filterClass, setFilterClass] = useState(() => searchParams.get('class') || '');
  const [filterFunding, setFilterFunding] = useState(() => searchParams.get('funding') || '');
  const [filterSchoolDistrict, setFilterSchoolDistrict] = useState(() => searchParams.get('district') || '');
  const [filterMedicalDue, setFilterMedicalDue] = useState(() => searchParams.get('medical') || '');
  const [filterSpedIndiv, setFilterSpedIndiv] = useState(() => searchParams.get('sped') || '');
  const [filterPacket, setFilterPacket] = useState(() => searchParams.get('packet') || '');
  // student_id → packet overall status ('complete'/'in_progress'/'not_sent').
  // null = unavailable (viewer role or load error) → packet column/filter hidden.
  const [packetStatusById, setPacketStatusById] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [fundingCodes, setFundingCodes] = useState([]);
  const [schoolDistricts, setSchoolDistricts] = useState([]);
  const [showArchived, setShowArchived] = useState(() => searchParams.get('archived') === '1');
  // Capture searched=1 once at mount: the URL-mirror effect below rewrites the
  // query string (without `searched`) before the session finishes loading, so
  // reading searchParams later would miss the flag and skip the auto-search.
  const arrivedWithSearchedRef = useRef(searchParams.get('searched') === '1');
  const autoSearchDoneRef = useRef(false);
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const refetchSearchRef = useRef(false);

  useClearOnSiteChange(siteId, () => {
    refetchSearchRef.current = hasSearched;
    setStudents([]);
    setCurrentSession(null);
    setClassrooms([]);
    setFundingCodes([]);
    setSchoolDistricts([]);
    setPacketStatusById(null);
    setSessionLoading(true);
  });

  useEffect(() => {
    loadSession();
    // loadSession closes over the latest filters; site id is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    if (!currentSession?.id) {
      setClassrooms([]);
      setFundingCodes([]);
      setSchoolDistricts([]);
      return;
    }
    const load = async () => {
      try {
        const [classRes, fundingRes, districtRes] = await Promise.all([
          sessionAPI.getClassrooms(currentSession.id),
          sessionAPI.getFundingCodes(currentSession.id),
          sessionAPI.getSchoolDistricts(currentSession.id),
        ]);
        setClassrooms(classRes.data.results || classRes.data || []);
        setFundingCodes(fundingRes.data.results || fundingRes.data || []);
        setSchoolDistricts(districtRes.data.results || districtRes.data || []);
      } catch (e) {
        console.error('Error loading classrooms/funding codes/school districts:', e);
      }
    };
    load();
  }, [currentSession?.id]);

  // Packet completion status per student (Devorah: see packet status from the
  // main page without the separate dashboard link). Endpoint is admin/editor
  // gated — on 403 (viewer) or error, the packet column/filter stays hidden.
  useEffect(() => {
    if (!currentSession?.id) {
      setPacketStatusById(null);
      return;
    }
    let cancelled = false;
    api.get('/sessions/packet-dashboard/', { params: { session: currentSession.id } })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res.data.results || []).forEach((row) => {
          if (row.student_id != null) map[row.student_id] = row.overall || '';
        });
        setPacketStatusById(map);
      })
      .catch(() => {
        if (!cancelled) setPacketStatusById(null);
      });
    return () => { cancelled = true; };
  }, [currentSession?.id]);

  const loadSession = async () => {
    try {
      let session = null;
      try {
        const sessionResponse = await sessionAPI.getCurrentSession();
        session = sessionResponse.data;
      } catch (_) {
        const sessRes = await sessionAPI.getSessions();
        const list = sessRes.data.results || sessRes.data || [];
        if (list.length > 0) {
          await sessionAPI.setActiveSession(list[0].id);
          const curRes = await sessionAPI.getCurrentSession();
          session = curRes.data;
        }
      }
      setCurrentSession(session);
    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setSessionLoading(false);
    }
  };

  const handleSearch = async (opts = {}) => {
    if (!currentSession?.id) return;
    const includeArchived = opts.includeArchived ?? showArchived;
    setSearchLoading(true);
    try {
      // Archived/discharged students (is_active=False) are hidden unless the
      // "Show archived" toggle is on (server-side `discharge` roster filter).
      const studentsResponse = await studentAPI.getStudents(currentSession.id, {
        discharge: includeArchived ? 'all' : 'active',
      });
      const list = studentsResponse.data.results || studentsResponse.data || [];
      setStudents(list);
      setHasSearched(true);
    } catch (error) {
      console.error('Error searching students:', error);
      setStudents([]);
      setHasSearched(true);
    } finally {
      setSearchLoading(false);
    }
  };

  const toggleShowArchived = (checked) => {
    setShowArchived(checked);
    if (hasSearched) handleSearch({ includeArchived: checked });
  };

  // Mirror filter state + searched flag into the URL (replace, not push, so we
  // don't spam history) — back-navigation then restores this exact view.
  useEffect(() => {
    const p = {};
    if (filterLastName) p.last = filterLastName;
    if (filterFirstName) p.first = filterFirstName;
    if (filterStatus) p.status = filterStatus;
    if (filterClass) p.class = filterClass;
    if (filterFunding) p.funding = filterFunding;
    if (filterSchoolDistrict) p.district = filterSchoolDistrict;
    if (filterMedicalDue) p.medical = filterMedicalDue;
    if (filterSpedIndiv) p.sped = filterSpedIndiv;
    if (filterPacket) p.packet = filterPacket;
    if (showArchived) p.archived = '1';
    if (hasSearched) p.searched = '1';
    setSearchParams(p, { replace: true });
  }, [filterLastName, filterFirstName, filterStatus, filterClass, filterFunding,
      filterSchoolDistrict, filterMedicalDue, filterSpedIndiv, filterPacket, showArchived, hasSearched, setSearchParams]);

  // Auto-run the search once on mount when arriving with searched=1 in the URL
  // (i.e. returning via Back from a student profile).
  useEffect(() => {
    if (!currentSession?.id) return;
    if (refetchSearchRef.current) {
      refetchSearchRef.current = false;
      handleSearch();
      return;
    }
    if (autoSearchDoneRef.current) return;
    autoSearchDoneRef.current = true;
    if (arrivedWithSearchedRef.current && !hasSearched) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.id]);

  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const districtMatches = useCallback((studentDistrict, selectedDistrict) => {
    const normalizeDistrict = (str) =>
      (str || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const s = normalizeDistrict(studentDistrict);
    const d = normalizeDistrict(selectedDistrict);
    if (!d) return true;
    if (!s) return false;
    return s === d || s.startsWith(d) || d.startsWith(s);
  }, []);

  const filteredStudents = useMemo(() => {
    if (!students.length) return [];
    const last = (filterLastName || '').trim().toLowerCase();
    const first = (filterFirstName || '').trim().toLowerCase();
    const status = (filterStatus || '').trim();
    const cls = (filterClass || '').trim();
    const funding = (filterFunding || '').trim();
    const district = (filterSchoolDistrict || '').trim();
    const sped = (filterSpedIndiv || '').trim();
    const packet = (filterPacket || '').trim();
    return students.filter((s) => {
      if (last && !(s.last_name || '').toLowerCase().startsWith(last)) return false;
      if (first && !(s.first_name || '').toLowerCase().startsWith(first)) return false;
      if (status && s.status !== status) return false;
      if (cls === '__related_service__') {
        // "Related service / SEIT" is a service type, not "no class assigned":
        // a center-based student without a class must NOT match (Devorah,
        // "RS vs CTR" 2026-07-24).
        const svc = (s.service_type || '').toLowerCase();
        if (svc !== 'related_service' && svc !== 'seit') return false;
      } else if (cls && (s.class_num || '').trim() !== cls) return false;
      if (funding && (s.funding_code || '').trim() !== funding) return false;
      if (sped && (s.sped_indiv_code || '').trim() !== sped) return false;
      if (district && !districtMatches(s.school_district, district)) return false;
      if (filterMedicalDue && !passesMedicalDueFilter(s, filterMedicalDue)) return false;
      if (packet && packetStatusById) {
        const overall = packetStatusById[s.id] || 'not_sent';
        if (packet === 'not_complete') {
          if (overall === 'complete') return false;
        } else if (overall !== packet) return false;
      }
      return true;
    });
  }, [students, filterLastName, filterFirstName, filterStatus, filterClass, filterFunding, filterSpedIndiv, filterSchoolDistrict, filterMedicalDue, filterPacket, packetStatusById, districtMatches]);

  const clearFilters = () => {
    setFilterLastName('');
    setFilterFirstName('');
    setFilterStatus('');
    setFilterClass('');
    setFilterFunding('');
    setFilterSchoolDistrict('');
    setFilterMedicalDue('');
    setFilterSpedIndiv('');
    setFilterPacket('');
  };

  const hasActiveFilters = filterLastName || filterFirstName || filterStatus || filterClass || filterFunding || filterSchoolDistrict || filterMedicalDue || filterSpedIndiv || filterPacket;

  // SPEDINDIV options come from the loaded result set (same pattern as the Roster page).
  const spedIndivOptions = useMemo(
    () => [...new Set(students.map((s) => (s.sped_indiv_code || '').trim()).filter(Boolean))].sort(),
    [students],
  );

  const classroomByNum = useMemo(() => {
    const map = {};
    classrooms.forEach((c) => {
      const num = String(c.class_num ?? '').trim();
      if (num) map[num] = c;
    });
    return map;
  }, [classrooms]);

  // Class column: real class (with ratio, e.g. "3 (8:1:2)") when assigned;
  // "Related service"/"SEIT" only when that is the stored service type;
  // otherwise blank. Never default to "Related service" just because no
  // class is assigned (Devorah, "RS vs CTR" 2026-07-24).
  const classCellLabel = (s) => {
    const num = (s.class_num || '').trim();
    if (num) {
      const ratio = (classroomByNum[num]?.class_ratio || '').trim();
      return ratio ? `${num} (${ratio})` : num;
    }
    const svc = (s.service_type || '').toLowerCase();
    if (svc === 'related_service') return 'Related service';
    if (svc === 'seit') return 'SEIT';
    return '—';
  };

  const lastNames = useMemo(() => {
    const set = new Set();
    students.forEach((s) => {
      const n = (s.last_name || '').trim();
      if (n) set.add(n);
    });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [students]);

  const firstNames = useMemo(() => {
    const set = new Set();
    students.forEach((s) => {
      const n = (s.first_name || '').trim();
      if (n) set.add(n);
    });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [students]);

  // Prefix filter: same for last name and first name — type "H" → names starting with H, "He" → starting with He
  const filterOptionsByPrefix = (options, state) => {
    const input = (state.inputValue || '').trim().toLowerCase();
    if (!input) return options;
    return options.filter((opt) => String(opt || '').toLowerCase().startsWith(input));
  };

  return (
    <Box>
      <AppHeader
        backTo="/"
        rightContent={
          <>
            <Typography variant="subtitle1" component="span" sx={{ flexGrow: 1, textAlign: 'left' }}>
              Students - {currentSession?.name || 'Loading...'}
            </Typography>
            {canEdit && (
              <Button color="inherit" startIcon={<Add />} onClick={() => setAddStudentOpen(true)}>
                Add Student
              </Button>
            )}
          </>
        }
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {sessionLoading ? (
          <Typography sx={{ py: 3 }}>Loading…</Typography>
        ) : (
        <>
        <Paper
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}
        >
          <FilterList sx={{ color: 'text.secondary', mr: 1 }} />
          <Autocomplete
            size="small"
            sx={{ minWidth: 180 }}
            options={lastNames}
            value={filterLastName || null}
            onChange={(_, v) => setFilterLastName(v || '')}
            inputValue={filterLastName}
            onInputChange={(_, v) => setFilterLastName(v || '')}
            filterOptions={filterOptionsByPrefix}
            getOptionLabel={(o) => (o ?? '')}
            renderInput={(params) => <TextField {...params} label="Last name" placeholder="e.g. H then He to narrow" />}
            freeSolo
            handleHomeEndKeys
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 180 }}
            options={firstNames}
            value={filterFirstName || null}
            onChange={(_, v) => setFilterFirstName(v || '')}
            inputValue={filterFirstName}
            onInputChange={(_, v) => setFilterFirstName(v || '')}
            filterOptions={filterOptionsByPrefix}
            getOptionLabel={(o) => (o ?? '')}
            renderInput={(params) => <TextField {...params} label="First name" placeholder="e.g. H then He to narrow" />}
            freeSolo
            handleHomeEndKeys
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Class</InputLabel>
            <Select
              label="Class"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
            >
              <MenuItem value="">All classes</MenuItem>
              <MenuItem value="__related_service__">Related service / SEIT only</MenuItem>
              {classrooms.map((c) => (
                <MenuItem key={c.id} value={c.class_num}>
                  {c.label || `${c.class_num} / ${c.class_size || '—'}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Funding code</InputLabel>
            <Select
              label="Funding code"
              value={filterFunding}
              onChange={(e) => setFilterFunding(e.target.value)}
            >
              <MenuItem value="">All funding codes</MenuItem>
              {fundingCodes.map((fc) => (
                <MenuItem key={fc.id} value={fc.code}>{fc.code}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>SPED INDIV</InputLabel>
            <Select
              label="SPED INDIV"
              value={filterSpedIndiv}
              onChange={(e) => setFilterSpedIndiv(e.target.value)}
            >
              <MenuItem value="">All SPED INDIV</MenuItem>
              {spedIndivOptions.map((code) => (
                <MenuItem key={code} value={code}>{code}</MenuItem>
              ))}
              {filterSpedIndiv && !spedIndivOptions.includes(filterSpedIndiv) && (
                <MenuItem value={filterSpedIndiv}>{filterSpedIndiv}</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>School district</InputLabel>
            <Select
              label="School district"
              value={filterSchoolDistrict}
              onChange={(e) => setFilterSchoolDistrict(e.target.value)}
            >
              <MenuItem value="">All school districts</MenuItem>
              {schoolDistricts.map((sd) => (
                <MenuItem key={sd.id} value={sd.name}>{sd.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Medical due</InputLabel>
            <Select
              label="Medical due"
              value={filterMedicalDue}
              onChange={(e) => setFilterMedicalDue(e.target.value)}
            >
              {MEDICAL_DUE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {packetStatusById && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Packet status</InputLabel>
              <Select
                label="Packet status"
                value={filterPacket}
                onChange={(e) => setFilterPacket(e.target.value)}
              >
                {PACKET_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={showArchived}
                onChange={(e) => toggleShowArchived(e.target.checked)}
              />
            )}
            label="Show archived"
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<Search />}
            disabled={searchLoading}
          >
            {searchLoading ? 'Searching…' : 'Search'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Description />}
            onClick={() => window.open(`/students/roster${currentSession?.id ? `?session=${currentSession.id}` : ''}`, '_blank', 'noopener,noreferrer')}
            disabled={!currentSession?.id}
          >
            Student roster
          </Button>
          {hasActiveFilters && (
            <Button size="small" startIcon={<Clear />} onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          {hasSearched && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Showing {filteredStudents.length} of {students.length} matches
            </Typography>
          )}
        </Paper>

        {hasSearched && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Funding</TableCell>
                  <TableCell>School district</TableCell>
                  <TableCell>Medical due</TableCell>
                  <TableCell>Date of Birth</TableCell>
                  <TableCell>Enrollment Date</TableCell>
                  <TableCell>Status</TableCell>
                  {packetStatusById && <TableCell>Packet</TableCell>}
                  <TableCell>Parent Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={packetStatusById ? 10 : 9} align="center" sx={{ py: 4 }}>
                      {students.length === 0
                        ? `No students in ${currentSession?.name || 'this session'}.`
                        : 'No students match the current filters.'}
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.map((student) => (
                  <TableRow
                    key={student.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/students/${student.id}`)}
                  >
                    <TableCell>
                      {student.first_name} {student.last_name}
                    </TableCell>
                    <TableCell>{classCellLabel(student)}</TableCell>
                    <TableCell>{student.funding_code || '-'}</TableCell>
                    <TableCell>{student.school_district || '-'}</TableCell>
                    <TableCell>{formatListDate(student.medical_due_date)}</TableCell>
                    <TableCell>{formatListDate(student.date_of_birth)}</TableCell>
                    <TableCell>{formatListDate(student.enrollment_date)}</TableCell>
                    <TableCell>{student.status}{student.is_active === false ? ' (archived)' : ''}</TableCell>
                    {packetStatusById && (
                      <TableCell>{PACKET_STATUS_LABELS[packetStatusById[student.id] || 'not_sent'] || '—'}</TableCell>
                    )}
                    <TableCell>{student.parent_email || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!hasSearched && (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            Enter filters if desired, then click <strong>Search</strong> to see children. Click a child to view full details.
          </Typography>
        )}
        </>
        )}
      </Container>

      <NewStudentForm
        open={addStudentOpen}
        onClose={() => setAddStudentOpen(false)}
        defaultSessionId={currentSession?.id}
        onSuccess={() => {
          setSnackbar({ open: true, message: 'Student created successfully.', severity: 'success' });
          if (hasSearched) handleSearch();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentList;
